import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, WMSTileLayer, Marker, Popup, GeoJSON, useMap, Polygon } from 'react-leaflet';
import { FiMap, FiFilter, FiNavigation, FiChevronRight, FiList, FiLayers, FiX, FiChevronDown, FiChevronUp, FiExternalLink } from 'react-icons/fi';
import { getJobMapPoints, getActiveTimeEntries } from '../services/api';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config';
import { buildQPublicUrl, hasQPublicUrl } from '../utils/parcelUtils';
import MeasurementToolbar from '../components/MeasurementTools/MeasurementToolbar';
import MeasurementController from '../components/MeasurementTools/MeasurementController';
import GpsEntryModal from '../components/MeasurementTools/GpsEntryModal';
import axios from 'axios';
import L from 'leaflet';
import * as EsriLeaflet from 'esri-leaflet';
import 'leaflet/dist/leaflet.css';

// Create axios instance with auth
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom colored markers
const createColoredIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color};
      width: 24px;
      height: 24px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 2px solid white;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24]
  });
};

// Live-crew marker: a pin with the crew count, green = running, amber = all paused
const createCrewIcon = (count, allPaused) => {
  const bg = allPaused ? '#f59e0b' : '#16a34a';
  return L.divIcon({
    className: 'crew-marker',
    html: `<div style="background:${bg};color:#fff;width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.4);border:2px solid #fff"><span style="transform:rotate(45deg);font-weight:700;font-size:13px">${count}</span></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -30],
  });
};

const crewFmt = (totalSec) => {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};
const crewSeconds = (e, nowMs) => {
  const base = e.accumulated_seconds || 0;
  if (e.is_paused) return base;
  const startStr = e.calc_clock_in || e.clock_in;
  if (!startStr) return base;
  const start = new Date(startStr + (startStr.endsWith('Z') ? '' : 'Z')).getTime();
  return base + Math.max(0, (nowMs - start) / 1000);
};

const gpsIcon = L.divIcon({
  className: 'gps-marker',
  html: `<div style="
    background-color: #dc2626;
    width: 28px;
    height: 28px;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
  "><span style="transform: rotate(45deg); color: white; font-size: 14px; font-weight: bold;">+</span></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28]
});

const statusColors = {
  'Pending': '#fbbf24',
  'Scheduled': '#60a5fa',
  'In Progress - Office Work': '#0ea5e9',
  'In Progress - Initial Field Work': '#a78bfa',
  'In Progress - Additional Field Work Required': '#7c3aed',
  'In Progress - Drafting': '#8b5cf6',
  'Survey Complete': '#34d399',
  'Needs Review': '#f97316',
  'Final Mark Up / Stake Lines': '#ec4899',
  'Needs Invoice': '#eab308',
  'Invoiced': '#fb923c',
  'Paid': '#10b981',
  'On Hold': '#64748b'
};

// Layer definitions
const OVERLAY_LAYERS = {
  femaFlood: {
    name: 'FEMA Flood Zones',
    type: 'esri-feature',
    url: 'https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/USA_Flood_Hazard_Reduced_Set_gdb/FeatureServer/0',
    defaultOpacity: 0.6,
    description: '100-Year Flood Zones',
    minZoom: 10,
    maxZoom: 18,
    style: (feature) => {
      // Color by flood zone type
      const colors = {
        'A': '#1e88e5',      // High risk
        'AE': '#1565c0',     // High risk with BFE
        'AH': '#0d47a1',     // High risk ponding
        'AO': '#42a5f5',     // High risk sheet flow
        'VE': '#d32f2f',     // Coastal high risk
        'X': '#90caf9',      // Moderate risk
        'default': '#64b5f6'
      };
      const zone = feature.properties?.FLD_ZONE || 'default';
      const color = colors[zone] || colors['default'];
      return {
        color: color,
        weight: 1,
        fillColor: color,
        fillOpacity: 0.4
      };
    }
  },
  nwiWetlands: {
    name: 'NWI Wetlands',
    type: 'esri-feature',
    url: 'https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/USA_Wetlands/FeatureServer/0',
    defaultOpacity: 0.6,
    description: 'National Wetlands Inventory',
    minZoom: 10,
    maxZoom: 18,
    style: {
      color: '#2e7d32',
      weight: 1,
      fillColor: '#4caf50',
      fillOpacity: 0.4
    }
  },
  usdaSoils: {
    name: 'USDA Soils',
    type: 'wms',
    url: 'https://SDMDataAccess.sc.egov.usda.gov/Spatial/SDM.wms',
    layers: 'MapunitPoly',
    defaultOpacity: 0.6,
    description: 'SSURGO Soil Map Units (zoom in to view)',
    minZoom: 12,
    maxZoom: 18
  },
  usgsHydro: {
    name: 'USGS Hydro',
    type: 'tile',
    url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSHydroCached/MapServer/tile/{z}/{y}/{x}',
    defaultOpacity: 0.7,
    description: 'Streams and rivers',
    maxZoom: 16
  }
};

const BASEMAP_LAYERS = {
  street: {
    name: 'Street (OSM)',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19
  },
  satellite: {
    name: 'Satellite (Esri)',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Esri, Maxar, Earthstar Geographics',
    maxZoom: 19
  },
  topo: {
    name: 'Topo (USGS)',
    url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}',
    attribution: 'USGS',
    maxZoom: 16
  }
};

// Move zoom control to bottom-right so it doesn't overlap measurement tools
function ZoomControlPosition() {
  const map = useMap();
  useEffect(() => {
    const zoomCtrl = L.control.zoom({ position: 'bottomright' });
    zoomCtrl.addTo(map);
    return () => {
      zoomCtrl.remove();
    };
  }, [map]);
  return null;
}

// Component to handle map resize when container changes
function MapResizer({ showJobsList }) {
  const map = useMap();

  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 100);
  }, [map]);

  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 350);
  }, [showJobsList, map]);

  return null;
}

// Component to track map zoom level
function ZoomTracker({ onZoomChange }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const handleZoom = () => {
      onZoomChange(map.getZoom());
    };

    map.on('zoomend', handleZoom);
    // Call immediately to set initial zoom
    handleZoom();

    return () => map.off('zoomend', handleZoom);
  }, [map, onZoomChange]);

  return null;
}

// Component to manage ESRI feature layers (Flood Zones, Wetlands)
function FeatureLayerController({ overlayVisibility, overlayOpacity }) {
  const map = useMap();
  const layersRef = useRef({});

  useEffect(() => {
    if (!map) return;

    Object.entries(OVERLAY_LAYERS).forEach(([key, config]) => {
      if (config.type !== 'esri-feature') return;

      const isVisible = overlayVisibility[key];
      const opacity = overlayOpacity[key] ?? config.defaultOpacity;

      if (isVisible && !layersRef.current[key]) {
        try {
          console.log(`Creating feature layer: ${key}`);

          const layerOptions = {
            url: config.url,
            style: config.style || {
              color: '#3388ff',
              weight: 1,
              fillOpacity: opacity
            },
            onEachFeature: (feature, layer) => {
              // Add popup with feature info
              const props = feature.properties || {};
              let popupContent = '<div style="max-width:200px">';
              if (props.FLD_ZONE) {
                popupContent += `<b>Flood Zone:</b> ${props.FLD_ZONE}<br>`;
              }
              if (props.ZONE_SUBTY) {
                popupContent += `<b>Subtype:</b> ${props.ZONE_SUBTY}<br>`;
              }
              if (props.SFHA_TF) {
                popupContent += `<b>Special Flood Hazard Area:</b> ${props.SFHA_TF}<br>`;
              }
              if (props.WETLAND_TYPE) {
                popupContent += `<b>Wetland Type:</b> ${props.WETLAND_TYPE}<br>`;
              }
              popupContent += '</div>';
              layer.bindPopup(popupContent);
            }
          };

          const layer = EsriLeaflet.featureLayer(layerOptions);

          layer.on('error', (e) => {
            console.error(`Feature layer ${key} error:`, e);
          });

          layer.on('load', () => {
            console.log(`Feature layer ${key} loaded`);
          });

          layer.addTo(map);
          layersRef.current[key] = layer;
          console.log(`Added feature layer: ${key}`);

        } catch (e) {
          console.error(`Error creating feature layer ${key}:`, e);
        }
      } else if (!isVisible && layersRef.current[key]) {
        try {
          map.removeLayer(layersRef.current[key]);
          delete layersRef.current[key];
          console.log(`Removed feature layer: ${key}`);
        } catch (e) {
          console.warn(`Error removing feature layer ${key}:`, e);
        }
      } else if (isVisible && layersRef.current[key]) {
        // Update style with new opacity
        try {
          const newStyle = typeof config.style === 'function'
            ? (feature) => ({ ...config.style(feature), fillOpacity: opacity })
            : { ...config.style, fillOpacity: opacity };
          layersRef.current[key].setStyle(newStyle);
        } catch (e) {
          console.warn(`Error updating style for ${key}:`, e);
        }
      }
    });
  }, [map, overlayVisibility, overlayOpacity]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.entries(layersRef.current).forEach(([key, layer]) => {
        if (map && map.hasLayer(layer)) {
          map.removeLayer(layer);
        }
      });
      layersRef.current = {};
    };
  }, [map]);

  return null;
}

// Component to manage county parcel layers
function ParcelLayerController({ selectedCounties, parcelConfig, mapZoom }) {
  const map = useMap();
  const layersRef = useRef({});
  const MIN_PARCEL_ZOOM = parcelConfig?.minZoom || 14;

  useEffect(() => {
    if (!map || !parcelConfig?.counties) return;

    const currentZoom = map.getZoom();

    Object.keys(parcelConfig.counties).forEach(countyKey => {
      const config = parcelConfig.counties[countyKey];
      const isSelected = selectedCounties.includes(countyKey);
      const shouldShow = isSelected && currentZoom >= MIN_PARCEL_ZOOM && config.enabled;

      if (shouldShow && !layersRef.current[countyKey]) {
        try {
          console.log(`Loading parcel layer: ${config.name}`);

          const defaultStyle = parcelConfig.defaultStyle || {
            color: '#ff6600',
            weight: 2,
            fillColor: '#ffcc00',
            fillOpacity: 0.15
          };

          const layer = EsriLeaflet.featureLayer({
            url: config.url,
            style: defaultStyle,
            onEachFeature: (feature, layer) => {
              const props = feature.properties || {};
              const parcelId = props[config.idField] || props.PARCEL_ID || props.PARID || 'N/A';
              const owner = props[config.ownerField] || props.OWNER || props.OWNER_NAME || 'Unknown';
              const acres = props[config.acreageField] || props.ACRES || props.ACREAGE || 'N/A';
              const address = props[config.addressField] || props.SITUS || props.ADDRESS || props.PROPERTY_ADDRESS || 'N/A';

              const taxUrl = config.taxAssessorUrl ? `${config.taxAssessorUrl}${parcelId}` : '#';

              let popupContent = `
                <div style="min-width:220px; font-size:13px;">
                  <div style="font-weight:600; font-size:14px; color:#1e40af; margin-bottom:8px; border-bottom:1px solid #e5e7eb; padding-bottom:5px;">
                    ${config.name} Parcel
                  </div>
                  <div style="margin-bottom:4px;"><strong>Parcel ID:</strong> ${parcelId}</div>
                  <div style="margin-bottom:4px;"><strong>Owner:</strong> ${owner}</div>
                  <div style="margin-bottom:4px;"><strong>Acres:</strong> ${acres}</div>
                  <div style="margin-bottom:8px;"><strong>Address:</strong> ${address}</div>
                  <a href="${taxUrl}" target="_blank" rel="noopener noreferrer"
                     style="display:inline-block; padding:6px 12px; background:#2563eb; color:white; text-decoration:none; border-radius:4px; font-size:12px;">
                    View Tax Record
                  </a>
                </div>
              `;
              layer.bindPopup(popupContent);
            }
          });

          layer.on('error', (e) => {
            console.error(`Parcel layer ${countyKey} error:`, e);
          });

          layer.addTo(map);
          layersRef.current[countyKey] = layer;
          console.log(`Added parcel layer: ${config.name}`);

        } catch (e) {
          console.error(`Error creating parcel layer ${countyKey}:`, e);
        }
      } else if (!shouldShow && layersRef.current[countyKey]) {
        try {
          map.removeLayer(layersRef.current[countyKey]);
          delete layersRef.current[countyKey];
          console.log(`Removed parcel layer: ${countyKey}`);
        } catch (e) {
          console.warn(`Error removing parcel layer ${countyKey}:`, e);
        }
      }
    });
  }, [map, selectedCounties, parcelConfig, mapZoom]);

  // Handle zoom changes
  useEffect(() => {
    if (!map) return;

    const handleZoom = () => {
      const currentZoom = map.getZoom();
      Object.keys(layersRef.current).forEach(countyKey => {
        const layer = layersRef.current[countyKey];
        if (currentZoom < MIN_PARCEL_ZOOM) {
          if (map.hasLayer(layer)) {
            map.removeLayer(layer);
          }
        } else {
          if (!map.hasLayer(layer) && selectedCounties.includes(countyKey)) {
            layer.addTo(map);
          }
        }
      });
    };

    map.on('zoomend', handleZoom);
    return () => map.off('zoomend', handleZoom);
  }, [map, selectedCounties, MIN_PARCEL_ZOOM]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.entries(layersRef.current).forEach(([key, layer]) => {
        if (map && map.hasLayer(layer)) {
          map.removeLayer(layer);
        }
      });
      layersRef.current = {};
    };
  }, [map]);

  return null;
}

// Toggle switch component
function ToggleSwitch({ checked, onChange, disabled }) {
  return (
    <button
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        border: 'none',
        backgroundColor: checked ? '#2563eb' : '#d1d5db',
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.2s',
        opacity: disabled ? 0.5 : 1,
        flexShrink: 0
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '2px',
          left: checked ? '22px' : '2px',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          backgroundColor: 'white',
          transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
        }}
      />
    </button>
  );
}

function Map() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  // Live Crews layer
  const [showLiveCrews, setShowLiveCrews] = useState(() => new URLSearchParams(window.location.search).get('crews') === '1');
  const [crewEntries, setCrewEntries] = useState([]);
  const [crewNow, setCrewNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [mapCenter, setMapCenter] = useState([34.0522, -84.5]);
  const [mapZoom, setMapZoom] = useState(9);
  const [boundaries, setBoundaries] = useState({});
  const [showBoundaries, setShowBoundaries] = useState(true);
  const [parcels, setParcels] = useState([]);
  const [showParcels, setShowParcels] = useState(true);
  const [selectedParcel, setSelectedParcel] = useState(null);

  // Measurement tools state
  const [activeMeasureTool, setActiveMeasureTool] = useState(null);
  const [measurementData, setMeasurementData] = useState({
    distancePoints: [], distanceTotal: 0, distanceSegments: [],
    areaPoints: [], areaValue: 0, areaPerimeter: 0,
    cursorCoords: null
  });
  const [showGpsModal, setShowGpsModal] = useState(false);
  const [gpsMarkers, setGpsMarkers] = useState([]);

  const clearMeasurement = useCallback(() => {
    setMeasurementData({
      distancePoints: [], distanceTotal: 0, distanceSegments: [],
      areaPoints: [], areaValue: 0, areaPerimeter: 0,
      cursorCoords: null
    });
  }, []);

  const finishMeasurement = useCallback(() => {
    // For mobile: finish active measurement
    if (activeMeasureTool === 'distance') {
      // Distance is already calculated, just stop
      setActiveMeasureTool(null);
    }
    // Area close is handled in the controller
  }, [activeMeasureTool]);

  const [showJobsList, setShowJobsList] = useState(() => {
    const saved = localStorage.getItem('mapShowJobsList');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Layer control state
  const [showLayerPanel, setShowLayerPanel] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    basemap: true,
    overlays: true,
    parcels: true
  });

  // Basemap state
  const [activeBasemap, setActiveBasemap] = useState(() => {
    return localStorage.getItem('mapActiveBasemap') || 'street';
  });

  // Overlay visibility state
  const [overlayVisibility, setOverlayVisibility] = useState(() => {
    const saved = localStorage.getItem('mapOverlayVisibility');
    if (saved) {
      return JSON.parse(saved);
    }
    return {
      femaFlood: false,
      nwiWetlands: false,
      usdaSoils: false,
      usgsHydro: false
    };
  });

  // Overlay opacity state
  const [overlayOpacity, setOverlayOpacity] = useState(() => {
    const saved = localStorage.getItem('mapOverlayOpacity');
    if (saved) {
      return JSON.parse(saved);
    }
    const defaults = {};
    Object.entries(OVERLAY_LAYERS).forEach(([key, config]) => {
      defaults[key] = config.defaultOpacity;
    });
    return defaults;
  });

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);

  // County parcel state
  const [parcelConfig, setParcelConfig] = useState(null);
  const [selectedCounties, setSelectedCounties] = useState(() => {
    const saved = localStorage.getItem('mapSelectedCounties');
    return saved ? JSON.parse(saved) : [];
  });
  const [showCountySelector, setShowCountySelector] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const isCapacitor = window.Capacitor?.isNativePlatform?.() || false;
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isCapacitor || isSmallScreen);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem('mapShowJobsList', JSON.stringify(showJobsList));
  }, [showJobsList]);

  useEffect(() => {
    localStorage.setItem('mapActiveBasemap', activeBasemap);
  }, [activeBasemap]);

  useEffect(() => {
    localStorage.setItem('mapOverlayVisibility', JSON.stringify(overlayVisibility));
  }, [overlayVisibility]);

  useEffect(() => {
    localStorage.setItem('mapOverlayOpacity', JSON.stringify(overlayOpacity));
  }, [overlayOpacity]);

  // Save selected counties to localStorage
  useEffect(() => {
    localStorage.setItem('mapSelectedCounties', JSON.stringify(selectedCounties));
  }, [selectedCounties]);

  // Fetch parcel config on mount
  useEffect(() => {
    const fetchParcelConfig = async () => {
      try {
        const response = await api.get('/parcel-config');
        setParcelConfig(response.data);
        console.log('Loaded parcel config with', Object.keys(response.data.counties || {}).length, 'counties');
      } catch (error) {
        console.error('Error loading parcel config:', error);
      }
    };
    fetchParcelConfig();
  }, []);

  const statuses = [
    'All',
    'Pending',
    'Scheduled',
    'In Progress - Office Work',
    'In Progress - Initial Field Work',
    'Cancelled',
    'In Progress - Additional Field Work Required',
    'In Progress - Drafting',
    'Survey Complete',
    'Needs Review',
    'Final Mark Up / Stake Lines',
    'Needs Invoice',
    'Invoiced',
    'Paid'
  ];

  useEffect(() => {
    loadJobs();
  }, []);

  useEffect(() => {
    filterJobs();
  }, [jobs, statusFilter]);

  // Live Crews: poll active timers (every 20s) and tick the displayed timers (every 1s) while the layer is on
  useEffect(() => {
    if (!showLiveCrews) return;
    let cancelled = false;
    const fetchCrews = async () => {
      try {
        const r = await getActiveTimeEntries();
        if (!cancelled) setCrewEntries((r.data?.time_entries || []).filter(e => !e.clock_out && e.job_id));
      } catch (e) { /* keep prior */ }
    };
    fetchCrews();
    const poll = setInterval(fetchCrews, 20000);
    const tick = setInterval(() => setCrewNow(Date.now()), 1000);
    return () => { cancelled = true; clearInterval(poll); clearInterval(tick); };
  }, [showLiveCrews]);

  const loadJobs = async () => {
    try {
      const response = await getJobMapPoints();
      const jobsWithCoords = response.data.jobs.filter(
        job => job.latitude && job.longitude
      );
      setJobs(jobsWithCoords);

      if (jobsWithCoords.length > 0) {
        setMapCenter([jobsWithCoords[0].latitude, jobsWithCoords[0].longitude]);
      }

      loadBoundaries(jobsWithCoords);
      loadParcels();
      setLoading(false);
    } catch (error) {
      console.error('Error loading jobs:', error);
      setLoading(false);
    }
  };

  const loadParcels = async () => {
    try {
      const response = await api.get('/map/parcels');
      setParcels(response.data.parcels || []);
    } catch (error) {
      console.error('Error loading parcels:', error);
    }
  };

  const loadBoundaries = async (jobsList) => {
    const boundaryPromises = jobsList
      .filter(job => job.boundary_file)
      .map(async (job) => {
        try {
          const response = await api.get(`/jobs/${job.id}/boundary`);
          return { jobId: job.id, geojson: response.data, status: job.status };
        } catch (error) {
          console.error(`Error loading boundary for job ${job.id}:`, error);
          return null;
        }
      });

    const results = await Promise.all(boundaryPromises);
    const boundaryMap = {};
    results.forEach(result => {
      if (result && result.geojson) {
        boundaryMap[result.jobId] = { geojson: result.geojson, status: result.status };
      }
    });
    setBoundaries(boundaryMap);
  };

  const filterJobs = () => {
    if (statusFilter === 'All') {
      setFilteredJobs(jobs);
    } else {
      setFilteredJobs(jobs.filter(job => job.status === statusFilter));
    }
  };

  const openInGoogleMaps = (lat, lng, address) => {
    const query = address ? encodeURIComponent(address) : `${lat},${lng}`;
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
  };

  const getBoundaryStyle = (jobId) => {
    const boundary = boundaries[jobId];
    const color = boundary ? (statusColors[boundary.status] || '#6b7280') : '#6b7280';
    return {
      color: color,
      weight: 3,
      opacity: 0.8,
      fillColor: color,
      fillOpacity: 0.15
    };
  };

  const getFilteredBoundaries = () => {
    if (statusFilter === 'All') {
      return Object.entries(boundaries);
    }
    return Object.entries(boundaries).filter(([jobId, data]) =>
      data.status === statusFilter
    );
  };

  const toggleOverlay = (key) => {
    setOverlayVisibility(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const updateOpacity = (key, value) => {
    setOverlayOpacity(prev => ({
      ...prev,
      [key]: parseFloat(value)
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleCounty = (countyKey) => {
    setSelectedCounties(prev => {
      if (prev.includes(countyKey)) {
        return prev.filter(k => k !== countyKey);
      } else {
        return [...prev, countyKey];
      }
    });
  };

  const activeOverlayCount = Object.values(overlayVisibility).filter(Boolean).length;

  if (loading) {
    return <div className="loading">Loading map...</div>;
  }

  const currentBasemap = BASEMAP_LAYERS[activeBasemap];

  return (
    <div className="map-page" style={{ position: 'relative', zIndex: 1 }}>
      <div className="page-header">
        <div>
          <h1><FiMap /> Job Map</h1>
          <p className="subtitle">{filteredJobs.length} jobs with location data</p>
        </div>
      </div>

      {/* Status Filter */}
      <div className="filter-bar" style={{ marginBottom: '15px' }}>
        <FiFilter />
        <span>Filter by status:</span>
        <div className="filter-buttons">
          {statuses.map(status => (
            <button
              key={status}
              className={`filter-btn ${statusFilter === status ? 'active' : ''}`}
              onClick={() => setStatusFilter(status)}
            >
              {status}
              {status !== 'All' && (
                <span style={{
                  display: 'inline-block',
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: statusColors[status],
                  marginLeft: '5px'
                }}></span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Map Legend & Controls */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        marginBottom: '15px',
        padding: '10px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
          <strong>Legend:</strong>
          {Object.entries(statusColors).map(([status, color]) => (
            <span key={status} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px' }}>
              <span style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: color
              }}></span>
              {status}
            </span>
          ))}
        </div>

        {/* Boundary Toggle */}
        {Object.keys(boundaries).length > 0 && (
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            padding: '6px 12px',
            backgroundColor: showBoundaries ? '#e3f2fd' : '#f5f5f5',
            borderRadius: '6px',
            border: showBoundaries ? '1px solid #90caf9' : '1px solid #ddd'
          }}>
            <input
              type="checkbox"
              checked={showBoundaries}
              onChange={(e) => setShowBoundaries(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '13px', fontWeight: '500' }}>
              Show Boundaries ({Object.keys(boundaries).length})
            </span>
          </label>
        )}
        {parcels.length > 0 && (
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            padding: '6px 12px',
            backgroundColor: showParcels ? '#fef2f2' : '#f5f5f5',
            borderRadius: '6px',
            border: showParcels ? '1px solid #fecaca' : '1px solid #ddd'
          }}>
            <input
              type="checkbox"
              checked={showParcels}
              onChange={(e) => setShowParcels(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '13px', fontWeight: '500', color: '#e11d48' }}>
              Show Parcels ({parcels.length})
            </span>
          </label>
        )}
        {user?.can_manage_payroll && (
          <label style={{
            display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
            padding: '6px 12px', backgroundColor: showLiveCrews ? '#dcfce7' : '#f5f5f5',
            borderRadius: '6px', border: showLiveCrews ? '1px solid #86efac' : '1px solid #ddd'
          }}>
            <input type="checkbox" checked={showLiveCrews} onChange={(e) => setShowLiveCrews(e.target.checked)} style={{ cursor: 'pointer' }} />
            <span style={{ fontSize: '13px', fontWeight: '500', color: '#15803d' }}>
              👷 Live Crews{showLiveCrews && crewEntries.length > 0 ? ` (${crewEntries.length})` : ''}
            </span>
          </label>
        )}
      </div>

      {filteredJobs.length > 0 ? (
        <div style={{ position: 'relative', height: 'calc(100vh - 300px)', minHeight: '500px' }}>
          {/* Measurement Toolbar */}
          <MeasurementToolbar
            activeTool={activeMeasureTool}
            onToolChange={setActiveMeasureTool}
            measurementData={measurementData}
            onClear={clearMeasurement}
            onFinish={finishMeasurement}
            onOpenGps={() => setShowGpsModal(true)}
            isMobile={isMobile}
          />

          {/* Layer Control Button */}
          <button
            onClick={() => setShowLayerPanel(!showLayerPanel)}
            style={{
              position: 'absolute',
              left: '70px',
              top: '15px',
              zIndex: 99999,
              backgroundColor: showLayerPanel ? '#1e40af' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
              fontWeight: '500',
              minHeight: '44px'
            }}
            title="Layer Controls"
          >
            <FiLayers size={18} />
            <span style={{ fontSize: '14px' }}>Layers</span>
            {activeOverlayCount > 0 && (
              <span style={{
                backgroundColor: '#fbbf24',
                color: '#1e3a8a',
                borderRadius: '10px',
                padding: '2px 8px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {activeOverlayCount}
              </span>
            )}
          </button>

          {/* Layer Control Panel */}
          {showLayerPanel && (
            <div style={{
              position: 'absolute',
              left: '70px',
              top: '60px',
              zIndex: 99998,
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              width: isMobile ? 'calc(100% - 30px)' : '320px',
              maxWidth: '350px',
              maxHeight: isMobile ? '60vh' : '70vh',
              overflowY: 'auto',
              overflowX: 'hidden'
            }}>
              {/* Panel Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '15px',
                borderBottom: '1px solid #e5e7eb',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                zIndex: 1
              }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Map Layers</h3>
                <button
                  onClick={() => setShowLayerPanel(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#6b7280'
                  }}
                >
                  <FiX size={20} />
                </button>
              </div>

              {/* Basemap Section */}
              <div style={{ borderBottom: '1px solid #e5e7eb' }}>
                <button
                  onClick={() => toggleSection('basemap')}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}
                >
                  <span>Basemap</span>
                  {expandedSections.basemap ? <FiChevronUp /> : <FiChevronDown />}
                </button>

                {expandedSections.basemap && (
                  <div style={{ padding: '0 15px 15px' }}>
                    {Object.entries(BASEMAP_LAYERS).map(([key, config]) => (
                      <label
                        key={key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px 12px',
                          marginBottom: '6px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backgroundColor: activeBasemap === key ? '#eff6ff' : '#f9fafb',
                          border: activeBasemap === key ? '2px solid #2563eb' : '2px solid transparent',
                          transition: 'all 0.2s'
                        }}
                      >
                        <input
                          type="radio"
                          name="basemap"
                          checked={activeBasemap === key}
                          onChange={() => setActiveBasemap(key)}
                          style={{
                            width: '18px',
                            height: '18px',
                            accentColor: '#2563eb'
                          }}
                        />
                        <span style={{
                          fontSize: '14px',
                          fontWeight: activeBasemap === key ? '500' : '400',
                          color: activeBasemap === key ? '#1e40af' : '#374151'
                        }}>
                          {config.name}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Overlays Section */}
              <div>
                <button
                  onClick={() => toggleSection('overlays')}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}
                >
                  <span>Data Overlays</span>
                  {expandedSections.overlays ? <FiChevronUp /> : <FiChevronDown />}
                </button>

                {expandedSections.overlays && (
                  <div style={{ padding: '0 15px 15px' }}>
                    {Object.entries(OVERLAY_LAYERS).map(([key, config]) => (
                      <div
                        key={key}
                        style={{
                          padding: '12px',
                          marginBottom: '8px',
                          borderRadius: '8px',
                          backgroundColor: overlayVisibility[key] ? '#f0fdf4' : '#f9fafb',
                          border: overlayVisibility[key] ? '1px solid #86efac' : '1px solid #e5e7eb'
                        }}
                      >
                        {/* Layer toggle row */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: overlayVisibility[key] ? '10px' : '0'
                        }}>
                          <div>
                            <div style={{
                              fontSize: '14px',
                              fontWeight: '500',
                              color: overlayVisibility[key] ? '#166534' : '#374151'
                            }}>
                              {config.name}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                              {config.description}
                            </div>
                          </div>
                          <ToggleSwitch
                            checked={overlayVisibility[key]}
                            onChange={() => toggleOverlay(key)}
                          />
                        </div>

                        {/* Opacity slider - only show when layer is visible */}
                        {overlayVisibility[key] && (
                          <div style={{ marginTop: '8px' }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              fontSize: '12px',
                              color: '#6b7280',
                              marginBottom: '4px'
                            }}>
                              <span>Opacity</span>
                              <span>{Math.round((overlayOpacity[key] || config.defaultOpacity) * 100)}%</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.1"
                              value={overlayOpacity[key] || config.defaultOpacity}
                              onChange={(e) => updateOpacity(key, e.target.value)}
                              style={{
                                width: '100%',
                                height: '6px',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                accentColor: '#2563eb'
                              }}
                            />
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Quick actions */}
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginTop: '12px',
                      paddingTop: '12px',
                      borderTop: '1px solid #e5e7eb'
                    }}>
                      <button
                        onClick={() => {
                          const allOn = {};
                          Object.keys(OVERLAY_LAYERS).forEach(k => allOn[k] = true);
                          setOverlayVisibility(allOn);
                        }}
                        style={{
                          flex: 1,
                          padding: '8px',
                          fontSize: '12px',
                          backgroundColor: '#e5e7eb',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          color: '#374151'
                        }}
                      >
                        Show All
                      </button>
                      <button
                        onClick={() => {
                          const allOff = {};
                          Object.keys(OVERLAY_LAYERS).forEach(k => allOff[k] = false);
                          setOverlayVisibility(allOff);
                        }}
                        style={{
                          flex: 1,
                          padding: '8px',
                          fontSize: '12px',
                          backgroundColor: '#e5e7eb',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          color: '#374151'
                        }}
                      >
                        Hide All
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* County Parcels Section */}
              {parcelConfig && Object.keys(parcelConfig.counties || {}).length > 0 && (
                <div style={{ borderTop: '1px solid #e5e7eb' }}>
                  <button
                    onClick={() => toggleSection('parcels')}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151'
                    }}
                  >
                    <span>
                      County Parcels
                      {selectedCounties.length > 0 && (
                        <span style={{
                          marginLeft: '8px',
                          backgroundColor: '#ff6600',
                          color: 'white',
                          borderRadius: '10px',
                          padding: '2px 8px',
                          fontSize: '11px'
                        }}>
                          {selectedCounties.length}
                        </span>
                      )}
                    </span>
                    {expandedSections.parcels ? <FiChevronUp /> : <FiChevronDown />}
                  </button>

                  {expandedSections.parcels && (
                    <div style={{ padding: '0 15px 15px' }}>
                      <div style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        marginBottom: '10px',
                        padding: '8px',
                        backgroundColor: '#fffbeb',
                        borderRadius: '6px',
                        border: '1px solid #fcd34d'
                      }}>
                        Parcels display when zoomed to level {parcelConfig.minZoom || 14}+
                      </div>

                      <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                        {Object.entries(parcelConfig.counties)
                          .filter(([_, config]) => config.enabled)
                          .sort((a, b) => a[1].name.localeCompare(b[1].name))
                          .map(([countyKey, config]) => (
                            <label
                              key={countyKey}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '8px 10px',
                                marginBottom: '4px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                backgroundColor: selectedCounties.includes(countyKey) ? '#fff7ed' : '#f9fafb',
                                border: selectedCounties.includes(countyKey) ? '1px solid #fb923c' : '1px solid transparent',
                                transition: 'all 0.2s'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedCounties.includes(countyKey)}
                                onChange={() => toggleCounty(countyKey)}
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  accentColor: '#ff6600'
                                }}
                              />
                              <span style={{
                                fontSize: '13px',
                                fontWeight: selectedCounties.includes(countyKey) ? '500' : '400',
                                color: selectedCounties.includes(countyKey) ? '#c2410c' : '#374151'
                              }}>
                                {config.name}
                              </span>
                            </label>
                          ))}
                      </div>

                      {/* Quick actions for parcels */}
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        marginTop: '10px',
                        paddingTop: '10px',
                        borderTop: '1px solid #e5e7eb'
                      }}>
                        <button
                          onClick={() => {
                            const allCounties = Object.entries(parcelConfig.counties)
                              .filter(([_, c]) => c.enabled)
                              .map(([key]) => key);
                            setSelectedCounties(allCounties);
                          }}
                          style={{
                            flex: 1,
                            padding: '6px',
                            fontSize: '11px',
                            backgroundColor: '#e5e7eb',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            color: '#374151'
                          }}
                        >
                          Select All
                        </button>
                        <button
                          onClick={() => setSelectedCounties([])}
                          style={{
                            flex: 1,
                            padding: '6px',
                            fontSize: '11px',
                            backgroundColor: '#e5e7eb',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            color: '#374151'
                          }}
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Jobs List Toggle Button */}
          <button
            onClick={() => setShowJobsList(!showJobsList)}
            style={{
              position: 'absolute',
              right: showJobsList ? '320px' : '15px',
              top: '15px',
              zIndex: 99999,
              backgroundColor: showJobsList ? '#6b7280' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
              transition: 'right 0.3s ease, background-color 0.2s ease',
              fontWeight: '500',
              minHeight: '44px'
            }}
            title={showJobsList ? 'Hide Jobs List' : 'Show Jobs List'}
          >
            {showJobsList ? <FiChevronRight size={18} /> : <FiList size={18} />}
            <span style={{ fontSize: '14px' }}>{showJobsList ? 'Hide List' : 'Show Jobs'}</span>
          </button>

          <div style={{ display: 'flex', gap: showJobsList ? '20px' : '0', height: '100%' }}>
            {/* Map Container */}
            <div style={{ flex: 1, borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd' }}>
              <MapContainer
                center={mapCenter}
                zoom={mapZoom}
                zoomControl={false}
                style={{ height: '100%', width: '100%' }}
              >
                <ZoomControlPosition />
                <MapResizer showJobsList={showJobsList} />
                <ZoomTracker onZoomChange={setMapZoom} />

                {/* Basemap Layer */}
                <TileLayer
                  key={activeBasemap}
                  attribution={currentBasemap.attribution}
                  url={currentBasemap.url}
                  maxNativeZoom={currentBasemap.maxZoom || 19}
                  maxZoom={20}
                />

                {/* ESRI Feature layers (FEMA Flood, Wetlands) */}
                <FeatureLayerController
                  overlayVisibility={overlayVisibility}
                  overlayOpacity={overlayOpacity}
                />

                {/* County Parcel Layers */}
                {parcelConfig && (
                  <ParcelLayerController
                    selectedCounties={selectedCounties}
                    parcelConfig={parcelConfig}
                    mapZoom={mapZoom}
                  />
                )}

                {/* Measurement Controller */}
                <MeasurementController
                  activeTool={activeMeasureTool}
                  measurementData={measurementData}
                  setMeasurementData={setMeasurementData}
                  gpsMarkers={gpsMarkers}
                />

                {/* GPS Markers */}
                {gpsMarkers.map((marker, idx) => (
                  <Marker key={`gps-${idx}`} position={[marker.lat, marker.lng]} icon={gpsIcon}>
                    <Popup>
                      <div style={{ minWidth: '180px' }}>
                        <h3 style={{ margin: '0 0 6px 0', fontSize: '15px', color: '#dc2626' }}>
                          {marker.label || `GPS Point ${idx + 1}`}
                        </h3>
                        <div style={{ fontSize: '13px', fontFamily: 'monospace', marginBottom: '8px' }}>
                          <div>Lat: {marker.lat.toFixed(6)}</div>
                          <div>Lng: {marker.lng.toFixed(6)}</div>
                        </div>
                        <button
                          onClick={() => setGpsMarkers(prev => prev.filter((_, i) => i !== idx))}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            width: '100%'
                          }}
                        >
                          Remove Pin
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                ))}

                {/* Tile Overlay Layers */}
                {Object.entries(OVERLAY_LAYERS).map(([key, config]) => (
                  overlayVisibility[key] && config.type === 'tile' && (
                    <TileLayer
                      key={key}
                      url={config.url}
                      opacity={overlayOpacity[key] || config.defaultOpacity}
                    />
                  )
                ))}

                {/* WMS Overlay Layers */}
                {Object.entries(OVERLAY_LAYERS).map(([key, config]) => (
                  overlayVisibility[key] && config.type === 'wms' && (
                    <WMSTileLayer
                      key={key}
                      url={config.url}
                      layers={config.layers}
                      format="image/png"
                      transparent={true}
                      opacity={overlayOpacity[key] || config.defaultOpacity}
                    />
                  )
                ))}

                {/* Render Boundary Polygons */}
                {showBoundaries && getFilteredBoundaries().map(([jobId, data]) => (
                  <GeoJSON
                    key={`boundary-${jobId}`}
                    data={data.geojson}
                    style={() => getBoundaryStyle(jobId)}
                    onEachFeature={(feature, layer) => {
                      const job = jobs.find(j => j.id === parseInt(jobId));
                      if (job) {
                        layer.bindPopup(`
                          <div style="min-width: 150px">
                            <strong>${job.job_number}</strong><br/>
                            <span style="color: #666">${job.client_name}</span><br/>
                            <span style="
                              display: inline-block;
                              margin-top: 5px;
                              background-color: ${statusColors[job.status]};
                              color: white;
                              padding: 2px 8px;
                              border-radius: 12px;
                              font-size: 11px;
                            ">${job.status}</span>
                          </div>
                        `);
                      }
                    }}
                  />
                ))}

                {/* Render Parcel Boundaries */}
                {showParcels && parcels.map(parcel => {
                  if (!parcel.geometry || parcel.geometry.length < 3) return null;

                  // Convert [lon, lat] to [lat, lon] for Leaflet
                  const positions = parcel.geometry.map(coord => [coord[1], coord[0]]);

                  return (
                    <Polygon
                      key={`parcel-${parcel.id}`}
                      positions={positions}
                      pathOptions={{
                        color: '#e11d48',
                        weight: 2,
                        fillColor: '#fda4af',
                        fillOpacity: 0.3
                      }}
                      eventHandlers={{
                        click: () => setSelectedParcel(parcel)
                      }}
                    >
                      <Popup>
                        <div style={{ minWidth: '280px', maxWidth: '350px' }}>
                          <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#e11d48' }}>
                            Parcel: {parcel.parcel_id}
                          </h3>

                          {parcel.job_number && (
                            <p style={{ margin: '4px 0', fontSize: '13px' }}>
                              <strong>Job:</strong>{' '}
                              <Link to={`/jobs/${parcel.job_id}`} style={{ color: '#2563eb' }}>
                                {parcel.job_number}
                              </Link>
                            </p>
                          )}

                          <div style={{ margin: '10px 0', padding: '8px', backgroundColor: '#fef2f2', borderRadius: '6px' }}>
                            <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: '600' }}>
                              {parcel.owner}
                            </p>
                            {parcel.owner_address && (
                              <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
                                {parcel.owner_address}
                              </p>
                            )}
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
                            <div>
                              <strong>Acres:</strong> {parcel.acres || '-'}
                            </div>
                            <div>
                              <strong>County:</strong> {parcel.county || '-'}
                            </div>
                            <div>
                              <strong>Land Lot:</strong> {parcel.land_lot || '-'}
                            </div>
                            <div>
                              <strong>District:</strong> {parcel.district || '-'}
                            </div>
                          </div>

                          {(parcel.deed_count > 0 || parcel.plat_count > 0) && (
                            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #fecaca' }}>
                              <div style={{ display: 'flex', gap: '15px', fontSize: '13px' }}>
                                {parcel.deed_count > 0 && (
                                  <div>
                                    <strong style={{ color: '#059669' }}>{parcel.deed_count} Deed(s):</strong>
                                    <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px', fontSize: '12px' }}>
                                      {parcel.deeds.slice(0, 3).map((deed, idx) => (
                                        <li key={idx}>{deed}</li>
                                      ))}
                                      {parcel.deeds.length > 3 && (
                                        <li style={{ color: '#666' }}>+{parcel.deeds.length - 3} more...</li>
                                      )}
                                    </ul>
                                  </div>
                                )}
                                {parcel.plat_count > 0 && (
                                  <div>
                                    <strong style={{ color: '#2563eb' }}>{parcel.plat_count} Plat(s):</strong>
                                    <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px', fontSize: '12px' }}>
                                      {parcel.plats.slice(0, 3).map((plat, idx) => (
                                        <li key={idx}>{plat}</li>
                                      ))}
                                      {parcel.plats.length > 3 && (
                                        <li style={{ color: '#666' }}>+{parcel.plats.length - 3} more...</li>
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Polygon>
                  );
                })}

                {/* Render Markers */}
                {filteredJobs.map(job => (
                  <Marker
                    key={job.id}
                    position={[job.latitude, job.longitude]}
                    icon={createColoredIcon(statusColors[job.status] || '#6b7280')}
                  >
                    <Popup>
                      <div style={{ minWidth: '200px' }}>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>{job.job_number}</h3>
                        <p style={{ margin: '4px 0', color: '#666' }}>{job.client_name}</p>
                        {job.property_address && (
                          <p style={{ margin: '4px 0', fontSize: '13px' }}>{job.property_address}</p>
                        )}
                        <p style={{ margin: '8px 0' }}>
                          <span style={{
                            backgroundColor: statusColors[job.status],
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            {job.status}
                          </span>
                          {job.boundary_file && (
                            <span style={{
                              marginLeft: '5px',
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px'
                            }}>
                              Boundary
                            </span>
                          )}
                        </p>
                        <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
                          <Link
                            to={`/jobs/${job.id}`}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#007bff',
                              color: 'white',
                              borderRadius: '4px',
                              textDecoration: 'none',
                              fontSize: '12px'
                            }}
                          >
                            View Job
                          </Link>
                          <button
                            onClick={() => openInGoogleMaps(job.latitude, job.longitude, job.property_address)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            <FiNavigation style={{ marginRight: '4px' }} />
                            Navigate
                          </button>
                          {(job.parcel_url || hasQPublicUrl(job.county)) && (
                            <button
                              onClick={() => {
                                const url = job.parcel_url || buildQPublicUrl(job.county);
                                if (url) window.open(url, '_blank');
                              }}
                              style={{
                                padding: '5px 10px',
                                backgroundColor: job.parcel_url ? '#16a34a' : '#ff6600',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              title={job.parcel_url ? 'Open saved parcel link' : `Search ${job.county} qPublic`}
                            >
                              <FiExternalLink style={{ marginRight: '4px' }} />
                              Parcel
                            </button>
                          )}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}

                {showLiveCrews && (() => {
                  const byJob = {};
                  crewEntries.forEach(e => { (byJob[e.job_id] = byJob[e.job_id] || []).push(e); });
                  return jobs.filter(j => byJob[j.id]).map(j => {
                    const crew = byJob[j.id];
                    return (
                      <Marker key={`crew-${j.id}`} position={[j.latitude, j.longitude]} icon={createCrewIcon(crew.length, crew.every(c => c.is_paused))}>
                        <Popup>
                          <div style={{ minWidth: 210 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                              <Link to={`/jobs/${j.id}`} style={{ fontWeight: 700, color: '#1d4ed8', textDecoration: 'none', fontSize: 15 }}>{j.job_number}</Link>
                              <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 700 }}>{crew.length} on site</span>
                            </div>
                            {j.property_address && <div style={{ fontSize: 12, color: '#64748b', margin: '2px 0 6px' }}>{j.property_address}</div>}
                            {crew.map(c => (
                              <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 13, padding: '2px 0' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.is_paused ? '#f59e0b' : '#16a34a', flexShrink: 0 }} />
                                  <span style={{ fontWeight: 600, color: '#0f172a' }}>{c.member_name || c.team_member_name || 'Crew'}</span>
                                  {c.work_type && <span style={{ color: '#64748b' }}>· {c.work_type}</span>}
                                  {c.work_order_number && <span style={{ color: '#2563eb' }}>· {c.work_order_number}</span>}
                                </span>
                                <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: c.is_paused ? '#b45309' : '#15803d' }}>{crewFmt(crewSeconds(c, crewNow))}</span>
                              </div>
                            ))}
                          </div>
                        </Popup>
                      </Marker>
                    );
                  });
                })()}
              </MapContainer>
            </div>

            {/* Job List Sidebar */}
            {showJobsList && (
              <div style={{
                width: '300px',
                flexShrink: 0,
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                padding: '15px',
                overflowY: 'auto'
              }}>
                <h3 style={{ marginTop: 0 }}>Jobs List</h3>
                {filteredJobs.map(job => (
                  <div
                    key={job.id}
                    style={{
                      backgroundColor: 'white',
                      padding: '12px',
                      borderRadius: '6px',
                      marginBottom: '10px',
                      borderLeft: `4px solid ${statusColors[job.status] || '#6b7280'}`,
                      cursor: 'pointer'
                    }}
                    onClick={() => setMapCenter([job.latitude, job.longitude])}
                  >
                    <h4 style={{ margin: '0 0 5px 0', fontSize: '14px' }}>
                      {job.job_number}
                      {job.boundary_file && (
                        <span style={{ marginLeft: '6px', fontSize: '12px' }} title="Has boundary file">📐</span>
                      )}
                    </h4>
                    <p style={{ margin: '0 0 5px 0', fontSize: '13px', color: '#666' }}>{job.client_name}</p>
                    {job.property_address && (
                      <p style={{ margin: '0', fontSize: '12px', color: '#888' }}>{job.property_address}</p>
                    )}
                    <div style={{ marginTop: '8px', display: 'flex', gap: '5px' }}>
                      <span style={{
                        fontSize: '11px',
                        backgroundColor: statusColors[job.status],
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '10px'
                      }}>
                        {job.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="empty-state" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <FiMap size={64} style={{ color: '#ccc' }} />
          <h2>No Jobs with Location Data</h2>
          <p>Add latitude and longitude coordinates to your jobs to see them on the map.</p>
          <p style={{ fontSize: '14px', color: '#666' }}>
            Edit a job and expand the "Map Coordinates" section to add location data.
          </p>
        </div>
      )}

      {/* GPS Coordinate Entry Modal */}
      {showGpsModal && (
        <GpsEntryModal
          onClose={() => setShowGpsModal(false)}
          onDropPin={(lat, lng, label) => {
            setGpsMarkers(prev => [...prev, { lat, lng, label }]);
            setShowGpsModal(false);
            setActiveMeasureTool(null);
          }}
        />
      )}
    </div>
  );
}

export default Map;
